import { Link } from 'react-router-dom';
import { Check, Clock, AlertTriangle } from 'lucide-react';
import { formatDate, isBlankDate } from '../utils/format';

interface SettlementDocument {
  documentType: 'invoice' | 'creditMemo';
  no: string;
  paid: boolean;
  paidDate?: string | null;
  dueDate?: string | null;
}

type StepState = 'done' | 'current' | 'held' | 'todo';

interface Step {
  title: string;
  detail?: React.ReactNode;
  state: StepState;
}

/**
 * A transaction's way to payment: registered → included in a settlement (or held, with the reason)
 * → settled on invoice X → paid on date Y.
 */
export default function SettlementTimeline({ order, settlementDocument }: { order: any; settlementDocument?: SettlementDocument | null }) {
  const settled = order.status !== 'Order';
  const held = !settled && (order.orderLate === 'Yes' || order.creditRisk === 'Yes');
  const holdReason = order.creditRisk === 'Yes' ? 'Kreditrisk' : 'Försenad';
  const invoiceNo = settlementDocument?.no || order.internalDocumentNo;

  const steps: Step[] = [
    { title: 'Registrerad', detail: formatDate(order.orderDate), state: 'done' },
    held
      ? {
          title: `Vilande – ${holdReason.toLowerCase()}`,
          detail: order.reasonDeniedOrLate || 'Tas med i avräkningen när det är löst',
          state: 'held',
        }
      : {
          title: settled ? 'Godkänd för avräkning' : 'Väntar på nästa avräkning',
          detail: settled ? undefined : 'Tas med i nästa avräkningskörning',
          state: settled ? 'done' : 'current',
        },
    {
      title: 'Avräknad',
      detail: settled ? (
        <>
          {settlementDocument ? (
            <Link
              to={`/invoices/${settlementDocument.documentType}/${encodeURIComponent(settlementDocument.no)}`}
              className="font-mono text-blue-700 hover:underline"
            >
              {invoiceNo}
            </Link>
          ) : (
            <span className="font-mono">{invoiceNo || '–'}</span>
          )}
          {!isBlankDate(order.settlementPostingDate) && ` · ${formatDate(order.settlementPostingDate)}`}
        </>
      ) : undefined,
      state: settled ? 'done' : 'todo',
    },
    {
      title: 'Utbetald',
      detail: settlementDocument?.paid
        ? formatDate(settlementDocument.paidDate)
        : settled && settlementDocument && !isBlankDate(settlementDocument.dueDate)
          ? `Förfaller ${formatDate(settlementDocument.dueDate)}`
          : undefined,
      state: settlementDocument?.paid ? 'done' : settled ? 'current' : 'todo',
    },
  ];

  return (
    <section aria-labelledby="timeline-heading" className="card mb-6 p-6">
      <h2 id="timeline-heading" className="text-[15px] font-semibold text-ink">
        Status
      </h2>
      <ol className="mt-5 grid gap-5 sm:grid-cols-4 sm:gap-3">
        {steps.map((step, i) => (
          <li key={step.title} className="relative flex gap-3 sm:flex-col">
            {i < steps.length - 1 && (
              <span
                aria-hidden
                className={`absolute left-[15px] top-8 h-[calc(100%-0.5rem)] w-px sm:left-8 sm:top-[15px] sm:h-px sm:w-[calc(100%-1.25rem)] ${
                  step.state === 'done' ? 'bg-blue-400' : 'bg-gray-200'
                }`}
              />
            )}
            <span
              className={`relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 ring-inset ${
                step.state === 'done'
                  ? 'bg-navy text-[#5ac9e3] ring-navy'
                  : step.state === 'current'
                    ? 'bg-blue-50 text-blue-700 ring-blue-300'
                    : step.state === 'held'
                      ? 'bg-[#fbf4e2] text-brass-ink ring-[#ecd9a4]'
                      : 'bg-white text-gray-300 ring-gray-200'
              }`}
            >
              {step.state === 'done' ? (
                <Check className="h-4 w-4" aria-hidden />
              ) : step.state === 'held' ? (
                <AlertTriangle className="h-4 w-4" aria-hidden />
              ) : (
                <Clock className="h-4 w-4" aria-hidden />
              )}
            </span>
            <span className="min-w-0">
              <span className={`block text-sm font-medium ${step.state === 'todo' ? 'text-gray-400' : 'text-ink'}`}>{step.title}</span>
              {step.detail && <span className="mt-0.5 block text-xs text-gray-600">{step.detail}</span>}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
